import axios from 'axios';
import config from '../config/environment';

// Create axios instance with default configuration
const api = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching
    config.params = {
      ...config.params,
      _t: Date.now(),
    };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    return Promise.reject(new Error(errorMessage));
  }
);

// API service methods
export const apiService = {
  // Health and system endpoints
  getHealth: () => axios.get('/health'),
  getSystemStats: () => api.get('/system/stats'),
  
  // Sandbox endpoints
  getSandboxStatus: () => api.get('/sandbox/status'),
  getSandboxEnvironments: () => api.get('/sandbox/environments'),
  executeCode: (codeRequest) => api.post('/sandbox/execute', codeRequest),
  getExecutionStatus: (executionId) => api.get(`/sandbox/executions/${executionId}`),
  cloneEnvironment: (environmentName, newName) => 
    api.post('/sandbox/clone-environment', null, {
      params: { environment_name: environmentName, new_name: newName }
    }),

  // Metrics endpoints
  getSystemMetrics: () => api.get('/metrics/system'),
  getMetricHistory: (metricType, hours = 1) => 
    api.get(`/metrics/history/${metricType}`, { params: { hours } }),
  getPerformanceMetrics: () => api.get('/metrics/performance'),
  getContainerMetrics: () => api.get('/metrics/containers'),
  getActiveAlerts: () => api.get('/alerts'),
  getDashboardData: () => api.get('/metrics/system'), // Use system metrics as dashboard data
  recordCustomMetric: (metricName, value, tags = {}) =>
    api.post('/metrics/custom-metric', null, {
      params: { metric_name: metricName, value },
      data: { tags }
    }),

  // Benchmark endpoints
  startLoadTest: (config) => api.post('/benchmark/load-test', config),
  getLoadTestStatus: (testId) => api.get(`/benchmark/load-test/${testId}`),
  getLoadTests: () => api.get('/benchmark/load-tests'),
  getBenchmarkSuites: () => api.get('/benchmark/benchmark-suites'),
  runBenchmarkSuite: (suiteName) => api.post(`/benchmark/benchmark-suite/${suiteName}`),
  getPerformanceReport: () => api.get('/benchmark/performance-report'),
};

// Utility functions
export const apiUtils = {
  // Format bytes to human readable format
  formatBytes: (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  },

  // Format duration in seconds to human readable format
  formatDuration: (seconds) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${(seconds % 60).toFixed(0)}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  },

  // Format number with commas
  formatNumber: (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  // Get status color based on value and thresholds
  getStatusColor: (value, warningThreshold = 70, criticalThreshold = 90) => {
    if (value >= criticalThreshold) return 'error';
    if (value >= warningThreshold) return 'warning';
    return 'success';
  },

  // Validate code execution request
  validateCodeRequest: (request) => {
    const errors = [];
    
    if (!request.code || request.code.trim() === '') {
      errors.push('Code cannot be empty');
    }
    
    if (!request.language) {
      errors.push('Language must be specified');
    }
    
    if (request.timeout && (request.timeout < 1 || request.timeout > 300)) {
      errors.push('Timeout must be between 1 and 300 seconds');
    }
    
    return errors;
  },

  // Validate load test configuration
  validateLoadTestConfig: (config) => {
    const errors = [];
    
    if (!config.target_url) {
      errors.push('Target URL is required');
    }
    
    if (config.concurrent_users < 1 || config.concurrent_users > 100) {
      errors.push('Concurrent users must be between 1 and 100');
    }
    
    if (config.duration_seconds < 10 || config.duration_seconds > 300) {
      errors.push('Duration must be between 10 and 300 seconds');
    }
    
    return errors;
  },
};

export default api;
